/**
 * WebSocket обработчики для Dashboard
 * Инвалидирует кэш dashboard при получении событий обновления данных
 */

import type { WebSocketEventRouter } from '@/shared/websocket/router';
import type { QueryClient } from '@tanstack/react-query';

interface Dependencies {
  queryClient: QueryClient;
}

export function registerDashboardWebSocketHandlers(
  router: WebSocketEventRouter,
  { queryClient }: Dependencies,
): () => void {
  const disposers: Array<() => void> = [];

  // Обработка события обновления статистики Dashboard
  disposers.push(
    router.on('dashboard.stats.updated', () => {
      // Инвалидируем все запросы dashboard
      queryClient.invalidateQueries({ queryKey: ['dashboardStats'] });
      queryClient.invalidateQueries({ queryKey: ['dashboardAlerts'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    }),
  );

  // Инвалидация при обновлении устройств (влияет на метрики устройств)
  disposers.push(
    router.on('device_status', () => {
      queryClient.invalidateQueries({ queryKey: ['dashboardStats'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'inactiveSwitches'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'inactiveRadios'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'inactiveOLT'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'inactiveONU'] });
    }),
  );

  // Инвалидация при обновлении задач (влияет на метрики задач)
  disposers.push(
    router.onMatch((type) => type.startsWith('task.'), () => {
      queryClient.invalidateQueries({ queryKey: ['dashboardStats'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'overdueTasks'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'myTasks'] });
    }),
  );

  // Инвалидация при обновлении клиентов (влияет на метрики клиентов)
  disposers.push(
    router.onMatch((type) => type.startsWith('customer.'), () => {
      queryClient.invalidateQueries({ queryKey: ['dashboardStats'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'pendingDisconnections'] });
    }),
  );

  // Инвалидация при обновлении модулей (влияет на системные метрики)
  disposers.push(
    router.onMatch((type) => type.startsWith('module.'), () => {
      queryClient.invalidateQueries({ queryKey: ['dashboardStats'] });
      queryClient.invalidateQueries({ queryKey: ['dashboardAlerts'] });
    }),
  );

  // Инвалидация при обновлении PON данных (влияет на виджеты PON)
  disposers.push(
    router.onMatch((type) => type.includes('pon') || type.includes('onu'), () => {
      queryClient.invalidateQueries({ queryKey: ['dashboardStats'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'badLevelONU'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'unknownONU'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'inactiveONU'] });
    }),
  );

  return () => {
    disposers.forEach((dispose) => dispose());
  };
}

